import { PageTitleRow } from "../../../components/layout/PageTitleRow";
import i18n from "../../../i18n";import { Trans } from "react-i18next";import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Search, MapPin, Briefcase, ShieldAlert, GraduationCap,
  Clock, FileText, History, Building2, KeyRound, Landmark, ChevronDown } from
"lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { Button } from "../../../components/ui/button";
import { ui } from "../../../components/ui";
import sessionDB from "../../../utils/sessionDB";
import { fetchEmployeeInfo, saveEmployeeProfile, saveEmergencyContact } from "../services/employeeInfoService";
import ProfileSectionEditor from "../components/ProfileSectionEditor";
import ProfilePhotoEditor from "../components/ProfilePhotoEditor";
import { ADDRESS_FIELDS, DATE_OF_BIRTH_FIELDS, PHONE_FIELDS, CONTACT_TYPE_FIELDS, EMERGENCY_FIELDS, employeePhotoSource } from "../services/profileFields";

// ESS employees can update their own personal details using the existing HRMS APIs.
// Team searches keep other employees read-only.

// Human labels for known keys; unknown keys fall back to a spaced Title Case
// of the key itself (see humanize()).
const LABELS = {
  empId: "Employee ID",
  firstName: "First name",
  middleName: "Middle name",
  lastName: "Last name",
  fullName: "Full name",
  dateOfBirth: "Date of birth",
  email: "Email",
  phone: "Phone",
  contactType: "Preferred contact",
  addressHouseNo: "House no.",
  addressStreetName: "Street",
  addressLocationArea: "Area / Location",
  addressDistrict: "District",
  addressState: "State",
  addressPincode: "Pincode",
  addressDigipin: "Digipin",
  addressGmapLink: "Google Maps link",
  createdOn: "Created on",
  designation: "Designation",
  department: "Department",
  division: "Division",
  gradeLevel: "Grade level",
  costCenter: "Cost center",
  workLocation: "Work location",
  empType: "Employment type",
  empStatus: "Status",
  dateOfJoin: "Date of joining",
  roleName: "Role",
  roleId: "Role IDs",
  skillName: "Skill",
  skillId: "Skill IDs",
  relationship: "Relationship",
  contactName: "Contact name",
  contactPhone: "Contact phone",
  emergencyContactName: "Emergency contact name",
  emergencyContact: "Emergency contact number",
  healthReport: "Health report",
  medicationInfo: "Medication details"
};

// Keys that are never shown as data rows anywhere (internal blobs/flags/audit).
const HIDDEN_KEYS = new Set([
"tenantId", "createdBy", "updatedBy", "updatedOn", "hasEmpPic",
"empPicBase64", "empPic", "photoBase64", "photoUrl", "key", "encrypted", "level"]
);

// Identifier keys (id / empId / *Id) are hidden in the non-master sections
// (they only clutter emergency/education/employment), but these two are kept
// visible there because their comma-list values ("1,2,3,4") are meaningful.
const ID_KEYS_TO_KEEP = new Set(["roleId", "skillId"]);

function isIdentifierKey(k) {
  return !ID_KEYS_TO_KEEP.has(k) && (k === "id" || k === "empId" || /Id$/.test(k));
}

function humanize(key) {
  if (LABELS[key]) return LABELS[key];
  return key
  .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
  .replace(/[_-]+/g, " ")
  .replace(/^\w/, (c) => c.toUpperCase());
}

function displayValue(v) {
  if (v == null) return "";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  return String(v);
}

function getInitials(name) {
  return (name || "")
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map((w) => w[0].toUpperCase())
  .join("") || "?";
}

// ─── Definition grid — renders present, non-hidden fields of `data` ──────────
// `hideIds` drops identifier keys (id/empId/*Id, except the kept ones); it's
// on for every section except the master card, whose own Employee ID is
// meaningful (see task: "remove id in all except master").
//
// `single` keeps the fields in one column. The paired cards are already half
// the page wide, and Tailwind's `sm:` tracks the viewport rather than the
// card, so without it a two-up card would split ~250px in half again.
const EDITABLE_BASIC_FIELDS = { dateOfBirth: DATE_OF_BIRTH_FIELDS, phone: PHONE_FIELDS, contactType: CONTACT_TYPE_FIELDS };

function FieldGrid({ data, only, exclude, hideIds, single, canEdit = false, onSave }) {
  const entries = useMemo(() => {
    if (!data || typeof data !== "object") return [];
    let keys = only ?? Object.keys(data);
    if (exclude) keys = keys.filter((k) => !exclude.includes(k));
    return keys
    .filter((k) => !HIDDEN_KEYS.has(k))
    .filter((k) => !(hideIds && isIdentifierKey(k)))
    .map((k) => [k, displayValue(data[k])])
    .filter(([key, v]) => v !== "" || (canEdit && EDITABLE_BASIC_FIELDS[key]));
  }, [data, only, exclude, hideIds, canEdit]);

  if (entries.length === 0) {
    return <p className="text-sm per-file-hint m-0"><Trans i18nKey="employeeInfo.noDetailsAvailable" /></p>;
  }

  return (
    <dl
      className={`grid gap-x-8 gap-y-3.5 m-0 ${
      single ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2"}`
      }>
      {entries.map(([key, value]) => {
        const isLink = /^https?:\/\//i.test(value);
        if (canEdit && EDITABLE_BASIC_FIELDS[key]) {
          return <ProfileSectionEditor key={key} fields={EDITABLE_BASIC_FIELDS[key]} record={data}
            canEdit={canEdit} onSave={onSave} editLabel={"Update " + humanize(key).toLowerCase()} compact
            renderShell={({ content, editAction }) => (
              <div className="flex flex-col gap-0.5 min-w-0">
                <dt className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide per-file-hint">
                  {humanize(key)}{editAction}
                </dt>
                <dd className="m-0 text-sm text-foreground wrap-break-word">{content}</dd>
              </div>
            )}>
            {value || "—"}
          </ProfileSectionEditor>;
        }
        return (
          <div key={key} className="flex flex-col gap-0.5 min-w-0">
            <dt className="text-[11px] font-semibold uppercase tracking-wide per-file-hint">
              {humanize(key)}
            </dt>
            <dd className="m-0 text-sm text-foreground wrap-break-word">
              {isLink ?
              <a
                href={value}
                target="_blank"
                rel="noreferrer"
                className="text-accent hover:underline">
                
                  {value}
                </a> :

              value
              }
            </dd>
          </div>);

      })}
    </dl>);

}

// ─── Repeated record blocks — one FieldGrid per record, hairline separated ───
// Used by every section that returns zero-or-more records. `error` is handled
// here rather than on SectionCard so a card holding two lists (Assets &
// Access) can fail one of them without blanking the other.
function RecordList({ records, error, empty }) {
  if (error) {
    return (
      <p className="text-sm per-file-hint m-0">
        <Trans i18nKey="employeeInfo.notAvailable" />{" "}
        <span className="text-(--color-text-secondary)/70">({error})</span>
      </p>);

  }

  if (!records || records.length === 0) {
    return (
      <p className="text-sm per-file-hint m-0">
        {empty ?? <Trans i18nKey="employeeInfo.noDetailsAvailable" />}
      </p>);

  }

  return (
    <div className="flex flex-col gap-5">
      {records.map((record, i) =>
      <div
        key={record.id ?? record.contactId ?? i}
        className={i > 0 ? "pt-5 border-t border-(--color-text-border)/50" : ""}>

          <FieldGrid data={record} hideIds single />
        </div>
      )}
    </div>);

}

// ─── Section card shell ──────────────────────────────────────────────────────
// The whole header is the toggle, so the hit target is the full card width
// rather than just the chevron. Cards start collapsed: ten sections open at
// once buries the profile, so the reader opens the one they came for.
function SectionCard({ icon: Icon, title, error, children, editAction, editing = false }) {
  const [open, setOpen] = useState(false);
  const panelId = `${useId()}-panel`;
  useEffect(() => { if (editing) setOpen(true); }, [editing]);

  return (
    <section className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden">
      <div className={"flex items-center " + (open ? "border-b border-(--color-text-border)/60" : "")}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={panelId}
        className="flex-1 min-w-0 px-5 sm:px-6 py-3.5 flex items-center gap-2.5 text-left cursor-pointer transition-colors hover:bg-workspace/30">

        <span className="grid place-items-center w-8 h-8 rounded-lg bg-primary/10 text-primary shrink-0">
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="text-base font-semibold text-foreground m-0 min-w-0 truncate">{title}</h2>
        <ChevronDown
          className={`h-4 w-4 ml-auto shrink-0 per-file-hint transition-transform ${
          open ? "" : "-rotate-90"}`
          } />

      </button>
      {editAction && <div className="pr-5 sm:pr-6">{editAction}</div>}
      </div>

      {open &&
      <div id={panelId} className="px-5 sm:px-6 py-5">
          {error ?
        <p className="text-sm per-file-hint m-0">
              <Trans i18nKey="employeeInfo.notAvailable" />{" "}
              <span className="text-(--color-text-secondary)/70">({error})</span>
            </p> :

        children
        }
        </div>
      }
    </section>);

}

// ─── Loading skeleton ────────────────────────────────────────────────────────
function InfoSkeleton() {
  return (
    <div className="flex flex-col gap-5">
      {[0, 1, 2].map((n) =>
      <div
        key={n}
        className="bg-white rounded-2xl border border-white/60 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] overflow-hidden">
        
          <div className="h-14 bg-workspace/40 animate-pulse" />
          <div className="p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            {[0, 1, 2, 3].map((i) =>
          <div key={i} className="h-9 rounded-lg bg-workspace/40 animate-pulse" />
          )}
          </div>
        </div>
      )}
    </div>);

}

// ─── Address keys live on the master record alongside the basic fields ───────
const ADDRESS_KEYS = [
"addressHouseNo", "addressStreetName", "addressLocationArea",
"addressDistrict", "addressState", "addressPincode",
"addressDigipin", "addressGmapLink"];

// Name is shown as the card heading, so it's left out of the grid below to
// avoid repeating it. (Role / employment-type / etc. come from the separate
// Current Employment endpoint, not the master record, so they show there.)
const BASIC_KEYS = [
"empId", "dateOfBirth", "email", "phone", "contactType", "createdOn"];


// Every card renders in both contexts. `showSearch` only governs the
// by-employee-ID lookup, which is the Team screen's reason for existing:
//   ESS  (own profile only) -> off
//   Team (CoreHR Management) -> on
export function EmployeeInfoPage({ showSearch = true } = {}) {
  const myEmpId = sessionDB.getEmpId();

  const [empId, setEmpId] = useState(myEmpId); // employee currently shown
  const [searchInput, setSearchInput] = useState(String(myEmpId ?? ""));
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(Boolean(myEmpId));
  const [error, setError] = useState(null);
  const reqIdRef = useRef(0);

  const load = useCallback((id) => {
    if (id == null || id === "") return;
    const reqId = ++reqIdRef.current;
    setLoading(true);
    setError(null);
    fetchEmployeeInfo(id)
    .then((data) => {
      if (reqIdRef.current !== reqId) return; // a newer search superseded this one
      setInfo(data);
    })
    .catch((err) => {
      if (reqIdRef.current !== reqId) return;
      setError(err.message || "Failed to load employee details.");
      setInfo(null);
    })
    .finally(() => {
      if (reqIdRef.current === reqId) setLoading(false);
    });
  }, []);

  useEffect(() => {load(empId);}, [empId, load]);

  const submitSearch = (e) => {
    e.preventDefault();
    const trimmed = searchInput.trim();
    if (trimmed) setEmpId(trimmed);
  };

  const master = info?.master ?? null;
  const fullName =
  master?.fullName ||
  [master?.firstName, master?.middleName, master?.lastName].filter(Boolean).join(" ") || (
  master ? `Employee #${master.empId ?? empId}` : "");
  const hasAddress = master && ADDRESS_KEYS.some((k) => master[k]);
  const canEdit = Boolean(myEmpId) && String(empId) === String(myEmpId) &&
    String(master?.empId ?? "") === String(myEmpId);
  const photoSrc = employeePhotoSource(master);

  // The update DTO omits photo bytes; only merge fields that were saved.
  function mergeSavedFields(record, updates, saved) {
    return {
      ...record,
      ...Object.fromEntries(Object.entries(updates).map(([key, value]) => [key, saved?.[key] ?? value])),
    };
  }

  async function savePersonalDetails(updates) {
    const requestId = reqIdRef.current;
    const saved = await saveEmployeeProfile(empId, updates);
    if (requestId !== reqIdRef.current) return;
    setInfo((current) => current ? {
      ...current, master: mergeSavedFields(current.master, updates, saved),
    } : current);
  }

  async function saveEmergencyDetails(updates) {
    const requestId = reqIdRef.current;
    const existing = info.emergency[0];
    const saved = await saveEmergencyContact(empId, updates, !existing);
    if (requestId !== reqIdRef.current) return;
    setInfo((current) => current ? {
      ...current,
      emergency: [mergeSavedFields(current.emergency[0] || { empId }, updates, saved)],
      errors: { ...current.errors, emergency: null },
    } : current);
  }

  return (
    <AppLayout>
      <div className="max-w-[1200px] mb-6">
        <PageTitleRow back={<PageBackLink to="/" />}>
          <h1 className={ui.pageTitle}><Trans i18nKey="employeeInfo.myProfile" /></h1>
        </PageTitleRow>
      </div>

      {/* Search by employee ID — Team context only */}
      {showSearch &&
      <form onSubmit={submitSearch} className="max-w-[1200px] flex items-center gap-2 mb-6">
        <div className="relative flex-1 max-w-[360px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 per-file-hint pointer-events-none" />
          <input data-page-search="true"
            className="w-full h-11 rounded-xl border border-(--color-text-border) pl-9 pr-3 text-sm text-foreground outline-hidden focus:border-accent focus:ring-2 focus:ring-accent/10 transition"
            type="text"
            inputMode="numeric"
            placeholder={i18n.t("formActions.searchByEmployeeId")}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)} />
          
        </div>
        <Button
          type="submit"
          className="h-11 px-5 rounded-xl bg-primary text-white hover:bg-accent">
          <Trans i18nKey="common.search" />
        
        </Button>
        {String(empId) !== String(myEmpId) && myEmpId != null && myEmpId !== "" &&
        <Button
          type="button"
          variant="outline"
          className="h-11 px-4 rounded-xl border-(--color-text-border) text-foreground hover:bg-workspace/40"
          onClick={() => {setSearchInput(String(myEmpId));setEmpId(myEmpId);}}>
            <Trans i18nKey="employeeInfo.myInfo" />
          
        </Button>
        }
      </form>
      }

      <div className="max-w-[1200px]">
        {!empId ?
        <div className="bg-white border border-(--color-text-border)/60 rounded-2xl px-5 py-4 text-sm per-file-hint">
            <Trans i18nKey="employeeInfo.enterAnEmployeeIdAboveToViewTheir" />
          </div> :
        loading ?
        <InfoSkeleton /> :
        error ?
        <div className="bg-white border border-error/30 rounded-2xl px-5 py-4 text-sm text-error">
            {error}
          </div> :
        master ?
        <div key={String(empId)} className="flex flex-col gap-5">
            {/* ── Basic Information ── */}
            <section className="bg-white rounded-2xl shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] border border-white/60 overflow-hidden">
              <div className="px-5 sm:px-6 py-5 flex items-center gap-4 border-b border-(--color-text-border)/60">
                <ProfilePhotoEditor photoSrc={photoSrc} fullName={fullName} initials={getInitials(fullName)}
                  canEdit={canEdit} onSave={savePersonalDetails} />
                <div className="min-w-0">
                  <h2 className="text-lg font-bold text-foreground m-0 truncate">{fullName}</h2>
                  <p className="text-sm per-file-hint m-0 mt-0.5">
                    <Trans i18nKey="employeeInfo.employeeId" /> {master.empId ?? empId}
                    {master.designation ? ` · ${master.designation}` : ""}
                  </p>
                </div>
              </div>
              <div className="px-5 sm:px-6 py-5">
                <FieldGrid data={master} only={BASIC_KEYS} canEdit={canEdit} onSave={savePersonalDetails} />
              </div>
            </section>

            {/* The ten detail cards pair up two-per-row below the profile card.
                Order here IS the layout: the grid fills row by row, so each
                consecutive pair sits side by side. `items-start` keeps a short
                card from stretching to match a tall neighbour. */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
              {/* ── Address · Emergency Contact ── */}
              <ProfileSectionEditor fields={ADDRESS_FIELDS} record={master} canEdit={canEdit}
                onSave={savePersonalDetails} editLabel="Update address"
                renderShell={({ content, editAction, editing }) => (
                  <SectionCard icon={MapPin} title={i18n.t("formActions.address")} editAction={editAction} editing={editing}>
                    {content}
                  </SectionCard>
                )}>
                  {hasAddress ? (
                    <FieldGrid data={master} only={ADDRESS_KEYS} single />
                  ) : (
                    <p className="text-sm per-file-hint m-0"><Trans i18nKey="employeeInfo.noAddressOnFile" /></p>
                  )}
              </ProfileSectionEditor>

                <ProfileSectionEditor fields={EMERGENCY_FIELDS} record={info.emergency[0]}
                  canEdit={canEdit && !info.errors.emergency} creating={!info.emergency.length}
                  onSave={saveEmergencyDetails}
                  editLabel={info.emergency.length ? "Update emergency contacts" : "Add emergency contacts"}
                  renderShell={({ content, editAction, editing }) => (
                    <SectionCard icon={ShieldAlert} title={i18n.t("employeeInfo.emergencyContact")} editAction={editAction} editing={editing}>
                      {content}
                    </SectionCard>
                  )}>
                  <RecordList records={info.emergency} error={info.errors.emergency}
                    empty={<Trans i18nKey="employeeInfo.noEmergencyContactOnFile" />} />
                </ProfileSectionEditor>

              {/* ── Current Employment · Position History ── */}
              <SectionCard icon={Briefcase} title={i18n.t("common.currentEmployment")} error={info.errors.employment}>
                <FieldGrid data={info.employment} hideIds single />
              </SectionCard>

              <SectionCard icon={History} title={i18n.t("onboarding.positionHistory")}>
                <RecordList records={info.positionHistory} error={info.errors.positionHistory} />
              </SectionCard>

              {/* ── Shift Mapping · Assets & Access ── */}
              <SectionCard icon={Clock} title={i18n.t("onboarding.shiftMapping")}>
                <RecordList records={info.shiftMapping} error={info.errors.shiftMapping} />
              </SectionCard>

              {/* Two lists in one card, each failing independently. */}
              <SectionCard icon={KeyRound} title={i18n.t("onboarding.assetsAccess")}>
                <div className="flex flex-col gap-5">
                  <div>
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide per-file-hint m-0 mb-2.5">
                      {i18n.t("onboarding.assetAllocation")}
                    </h3>
                    <RecordList records={info.assetAllocation} error={info.errors.assetAllocation} />
                  </div>
                  <div className="pt-5 border-t border-(--color-text-border)/50">
                    <h3 className="text-[11px] font-semibold uppercase tracking-wide per-file-hint m-0 mb-2.5">
                      {i18n.t("onboarding.accessCard")}
                    </h3>
                    <RecordList records={info.accessCard} error={info.errors.accessCard} />
                  </div>
                </div>
              </SectionCard>

              {/* ── Account Details · Documents ── */}
              <SectionCard icon={Landmark} title={i18n.t("onboarding.accountDetails")}>
                <RecordList records={info.accountDetails} error={info.errors.accountDetails} />
              </SectionCard>

              <SectionCard icon={FileText} title={i18n.t("onboarding.employeeDocuments")}>
                <RecordList records={info.documents} error={info.errors.documents} />
              </SectionCard>

              {/* ── Education · Previous Employment ── */}
              <SectionCard icon={GraduationCap} title={i18n.t("employeeInfo.education")}>
                <RecordList
                  records={info.education}
                  error={info.errors.education}
                  empty={<Trans i18nKey="employeeInfo.noEducationRecordsOnFile" />} />

              </SectionCard>

              <SectionCard icon={Building2} title={i18n.t("pagination.previousEmployment")}>
                <RecordList records={info.previousEmployment} error={info.errors.previousEmployment} />
              </SectionCard>
            </div>
          </div> :

        <div className="bg-white border border-(--color-text-border)/60 rounded-2xl px-5 py-4 text-sm per-file-hint">
            <Trans i18nKey="employeeInfo.noEmployeeFoundForId" /> {String(empId)}.
          </div>
        }
      </div>
    </AppLayout>);

}
